import { useState, useEffect, useCallback } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { monitorApi, SOURCE_PLATFORMS } from '@/lib/api/monitor'
import { Search, Download, ChevronLeft, ChevronRight, Loader2, FileText, ExternalLink, Globe } from 'lucide-react'
import { useAllProjects } from '@/lib/hooks/useProjects'

const emotionBadge = (emotion: number) => {
  if (emotion === 1) return { variant: 'success' as const, text: 'positive' }
  if (emotion === -1) return { variant: 'destructive' as const, text: 'negative' }
  return { variant: 'secondary' as const, text: 'neutral' }
}

function useProjectIdParam() {
  const location = useLocation()
  const [projectId, setProjectId] = useState('')
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    setProjectId(sp.get('projectId') || '')
  }, [location.pathname, location.search])
  return projectId
}

export function MonitorDataPage() {
  const { t } = useTranslation()
  const routeProjectId = useProjectIdParam()
  const { projects, groups, loading: projectsLoading } = useAllProjects()
  const [keyword, setKeyword] = useState('')
  const [timePeriod, setTimePeriod] = useState(7)
  const [emotionFilter, setEmotionFilter] = useState('')
  const [sourcePlatform, setSourcePlatform] = useState('')
  const [projectPlatforms, setProjectPlatforms] = useState<string[]>([])
  const [projectId, setProjectId] = useState(routeProjectId)
  const [projectName, setProjectName] = useState('')
  const [articles, setArticles] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<any>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const timeOptions = [
    { value: 1, label: t('monitor.hours24') },
    { value: 2, label: t('monitor.today') },
    { value: 3, label: t('monitor.yesterday') },
    { value: 4, label: t('monitor.days3') },
    { value: 5, label: t('monitor.days7') },
    { value: 6, label: t('monitor.days15') },
    { value: 7, label: t('monitor.days30') },
    { value: 8, label: t('monitor.days365') },
  ]

  const pageSize = 20

  // Update projectId from route search params or auto-select first project
  useEffect(() => {
    if (routeProjectId && routeProjectId !== projectId) {
      setProjectId(routeProjectId)
      setPage(1)
    } else if (!projectId && !routeProjectId && projects.length > 0) {
      setProjectId(projects[0].project_id)
      setPage(1)
    }
  }, [routeProjectId, projectId, projects])

  // Load project name and platforms
  useEffect(() => {
    if (projectId) {
      monitorApi.getProjectInfo(projectId).then((res: any) => {
        const info = res?.data || res
        setProjectName(info?.project_name || projectId)
        setProjectPlatforms(info?.source_platforms || [])
      }).catch(() => setProjectName(projectId))
    } else {
      setProjectPlatforms([])
    }
  }, [projectId])

  const handleSearch = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setErrorMsg('')
    try {
      const res: any = await monitorApi.getDataList({
        projectid: projectId,
        pagenum: page,
        searchkeyword: keyword,
        time_period: timePeriod,
        emotion: emotionFilter,
        classify: sourcePlatform,
      })
      // Check for API-level errors (code: -1 means auth error etc)
      if (res?.code === -1) {
        setErrorMsg(res?.msg || 'API error')
        setArticles([])
        setTotal(0)
        setLoading(false)
        return
      }
      const data = res?.data || res || []
      setArticles(Array.isArray(data) ? data : (data?.list || []))
      setTotal(res?.total || res?.count || 0)
    } catch (err: any) {
      console.error('Search failed:', err)
      setErrorMsg(err?.message || 'Request failed')
      setArticles([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, keyword, timePeriod, emotionFilter, sourcePlatform])

  // Load data when projectId, page, or any filter changes
  useEffect(() => {
    if (projectId) handleSearch()
  }, [projectId, page, keyword, timePeriod, emotionFilter, sourcePlatform])

  // Reset page when filters change (useEffect above will fire due to state change)
  const handleFilterChange = () => {
    if (page !== 1) setPage(1)
    // If already on page 1, the useEffect dependency change already triggered handleSearch
  }

  // Force search (for search button click when no filter state changed)
  const handleForceSearch = () => {
    if (page !== 1) setPage(1)
    else handleSearch()
  }

  const handleExport = async () => {
    try {
      const res: any = await monitorApi.exportData({
        projectid: projectId,
        searchkeyword: keyword,
        time_period: timePeriod,
        emotion: emotionFilter,
      })
      // Handle blob download
      if (res instanceof Blob) {
        const url = URL.createObjectURL(res)
        const a = document.createElement('a')
        a.href = url
        a.download = 'export.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  return (
    <div className="space-y-4">
      {/* Project selector + error message */}
      {projectId && projectName && (
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {t('monitor.projectName')}: <span className="font-medium text-foreground">{projectName}</span>
          </p>
        </div>
      )}

      {/* Project dropdown selector */}
      <div className="flex items-center gap-3">
        <select
          className="h-10 w-64 rounded-md border border-input bg-background px-3 text-sm"
          value={projectId}
          onChange={(e) => { setProjectId(e.target.value); setPage(1) }}
        >
          <option value="">{projectsLoading ? '加载中...' : '选择项目'}</option>
          {groups.map(g => (
            <optgroup key={g.group_id} label={g.group_name}>
              {projects.filter(p => p.group_id === g.group_id).map(p => (
                <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Error message */}
      {errorMsg && (
        <div className="rounded-lg bg-destructive/10 px-4 py-2 text-sm text-destructive">
          ⚠ {errorMsg}
        </div>
      )}

      {/* No project selected */}
      {!projectId && (
        <Card>
          <CardContent className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-2">
            <FileText className="h-12 w-12 opacity-30" />
            <p>{t('monitor.selectProject') || '请选择一个项目'}</p>
            {projectsLoading && <Loader2 className="h-5 w-5 animate-spin" />}
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      {projectId && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Input
                placeholder={t('common.search')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="w-64"
                onKeyDown={(e) => { if (e.key === 'Enter') handleForceSearch() }}
              />
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={timePeriod}
                onChange={(e) => { setTimePeriod(Number(e.target.value)); handleFilterChange() }}
              >
                {timeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={emotionFilter}
                onChange={(e) => { setEmotionFilter(e.target.value); handleFilterChange() }}
              >
                <option value="">{t('common.all')}</option>
                <option value="1">{t('monitor.positive')}</option>
                <option value="0">{t('monitor.neutral')}</option>
                <option value="-1">{t('monitor.negative')}</option>
              </select>
              <select
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={sourcePlatform}
                onChange={(e) => { setSourcePlatform(e.target.value); handleFilterChange() }}
              >
                <option value="">{t('common.all')}</option>
                {(projectPlatforms.length > 0 ? projectPlatforms : SOURCE_PLATFORMS.map(p => p.id)).map((pid) => {
                  const platform = SOURCE_PLATFORMS.find(p => p.id === pid)
                  const label = platform ? platform.name : pid
                  return <option key={pid} value={pid}>{label}</option>
                })}
              </select>
              {projectPlatforms.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Globe className="h-3 w-3 text-muted-foreground" />
                  {projectPlatforms.slice(0, 4).map((pid) => {
                    const platform = SOURCE_PLATFORMS.find(p => p.id === pid)
                    const label = platform ? platform.name : pid
                    return (
                      <Badge key={pid} variant="outline" className="text-xs font-normal">
                        {label}
                      </Badge>
                    )
                  })}
                  {projectPlatforms.length > 4 && (
                    <span className="text-xs text-muted-foreground">+{projectPlatforms.length - 4}</span>
                  )}
                </div>
              )}
              <Button onClick={handleForceSearch} disabled={loading || !projectId}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                {t('common.search')}
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={loading || !projectId}>
                <Download className="mr-2 h-4 w-4" />
                {t('common.export')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data table */}
      {projectId && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">{t('monitor.articleTitle')}</TableHead>
                  <TableHead>{t('monitor.sourceName')}</TableHead>
                  <TableHead>{t('monitor.sourcePlatform') || '来源平台'}</TableHead>
                  <TableHead>{t('monitor.publishTime')}</TableHead>
                  <TableHead>{t('monitor.emotion')}</TableHead>
                  <TableHead>{t('monitor.similarCount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((article) => {
                    const badge = emotionBadge(article.emotionalIndex ?? article.emotion)
                    return (
                      <TableRow
                        key={article.article_public_id || article.id}
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() => setSelectedArticle(article)}
                      >
                        <TableCell className="font-medium">
                          <span className="cursor-pointer hover:text-primary hover:underline">
                            {article.title}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{article.source_name || article.sourceName}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-normal">
                            {(() => {
                              const pid = article.source_platform
                              const platform = pid ? SOURCE_PLATFORMS.find(p => p.id === pid) : null
                              return platform ? platform.name : (pid || article.source_name || '-')
                            })()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{article.publish_time || article.publishTime}</TableCell>
                        <TableCell>
                          <Badge variant={badge.variant}>
                            {t(`monitor.${badge.text}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>{article.similar_count || 0}</TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {projectId && total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.total')} {total} {t('common.items')}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
              {t('common.prev')}
            </Button>
            <span className="text-sm">
              {t('common.page')} {page} {t('common.of')} {Math.ceil(total / pageSize)}
            </span>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(page + 1)}>
              {t('common.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Article detail dialog */}
      {selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedArticle(null)}>
          <div className="max-h-[80vh] w-[600px] overflow-y-auto rounded-lg bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold">{selectedArticle.title}</h3>
              <Button variant="ghost" size="icon" onClick={() => setSelectedArticle(null)}>
                ✕
              </Button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex gap-4 text-muted-foreground flex-wrap">
                <span>{t('monitor.sourceName')}: {selectedArticle.source_name || selectedArticle.sourceName}</span>
                <span>{t('monitor.sourcePlatform') || '来源平台'}: {(() => {
                  const pid = selectedArticle.source_platform
                  const platform = pid ? SOURCE_PLATFORMS.find(p => p.id === pid) : null
                  return platform ? platform.name : (pid || selectedArticle.source_name || '-')
                })()}</span>
                <span>{t('monitor.publishTime')}: {selectedArticle.publish_time || selectedArticle.publishTime}</span>
              </div>
              <Badge variant={emotionBadge(selectedArticle.emotionalIndex ?? selectedArticle.emotion).variant}>
                {t(`monitor.${emotionBadge(selectedArticle.emotionalIndex ?? selectedArticle.emotion).text}`)}
              </Badge>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="whitespace-pre-wrap">{selectedArticle.content || selectedArticle.summary || t('common.noData')}</p>
              </div>
              {(() => {
                const article = selectedArticle as any
                const url: string = article.url || ''
                if (!url) return null
                const pid = article.source_platform || ''
                const platform = SOURCE_PLATFORMS.find(p => p.id === pid)
                const platformName = platform?.name || '来源'
                // Detect search URLs
                const searchPatterns = [
                  'baidu.com/s?', 's.weibo.com/weibo?', 'weixin.sogou.com/weixin?',
                  'zhihu.com/search?', 'search.bilibili.com/all?', 'douyin.com/search/',
                  'kuaishou.com/search/video?', 'xiaohongshu.com/search_result?',
                  'channels.weixin.qq.com/search?', 'tieba.baidu.com/f?',
                ]
                const isSearchUrl = searchPatterns.some(p => url.includes(p))
                // Platforms that require login to view full content
                const needLoginPlatforms: Record<string, string> = {
                  weibo: '微博',
                  xiaohongshu: '小红书',
                  zhihu: '知乎',
                  douyin: '抖音',
                  bilibili: 'B站',
                  kuaishou: '快手',
                }
                const needLogin = needLoginPlatforms[pid]
                const linkText = isSearchUrl ? `在${platformName}中搜索` : '原文链接'
                return (
                  <div className="space-y-1">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                      <ExternalLink className="h-3 w-3" />
                      {linkText}
                    </a>
                    {needLogin && (
                      <p className="text-xs text-muted-foreground">⚠ 请登录{needLogin}账号后查看完整内容</p>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
